import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchSettings, adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import RichTextEditor from "@/components/RichTextEditor";
import { Loader2, Save, FileText } from "lucide-react";

const PAGES = [
  { key: "terms", label: "Terms of Use", path: "/terms" },
  { key: "privacy", label: "Privacy Policy", path: "/privacy" },
  { key: "disclaimer", label: "Disclaimer", path: "/disclaimer" },
  { key: "dpdp", label: "DPDP Act Notice", path: "/dpdp" },
];

export default function AdminLegal() {
  const { reload } = useSettings();
  const [vals, setVals] = useState(null);
  const [active, setActive] = useState("terms");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings().then((d) => setVals(d.legal_pages || {})).catch(() => setVals({}));
  }, []);

  const setField = (key, field, v) =>
    setVals((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings({ legal_pages: vals });
      await reload();
      toast.success("Legal pages saved");
    } catch (err) {
      toast.error("Could not save", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!vals) return <div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const page = vals[active] || {};

  return (
    <div data-testid="admin-legal" className="max-w-4xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-serif-latin text-2xl text-ink"><FileText className="h-5 w-5 text-saffron" /> Legal Pages</h1>
          <p className="mt-1 text-sm text-ink-3">Edit the Terms of Use, Privacy Policy and Disclaimer shown in the footer. Content supports rich formatting.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-legal-save"
          className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-line">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setActive(p.key)}
            data-testid={`admin-legal-tab-${p.key}`}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${active === p.key ? "border-saffron text-ink" : "border-transparent text-ink-3 hover:text-ink"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-5">
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-3">Page title</label>
          <input
            type="text"
            value={page.title || ""}
            onChange={(e) => setField(active, "title", e.target.value)}
            data-testid="admin-legal-title"
            className="w-full border border-line bg-[var(--surface)] px-3 py-2 text-ink outline-none focus:border-saffron"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-3">Last updated (optional)</label>
          <input
            type="text"
            value={page.updated || ""}
            placeholder="e.g. June 2026"
            onChange={(e) => setField(active, "updated", e.target.value)}
            data-testid="admin-legal-updated"
            className="w-full max-w-xs border border-line bg-[var(--surface)] px-3 py-2 text-ink outline-none focus:border-saffron"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-3">Body</label>
          <RichTextEditor
            key={active}
            value={page.body || ""}
            onChange={(html) => setField(active, "body", html)}
            placeholder="Write the page content…"
          />
        </div>
        <a href={PAGES.find((p) => p.key === active)?.path} target="_blank" rel="noreferrer" className="inline-block text-sm text-saffron link-underline">
          Preview {PAGES.find((p) => p.key === active)?.path} ↗
        </a>
      </div>
    </div>
  );
}
